import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

const BASE = 'techsavanna_pos.api.marketing_api';

const ENDPOINTS = {
  getCampaigns: `${BASE}.get_campaigns`,
  createCampaign: `${BASE}.create_campaign`,
  updateCampaign: `${BASE}.update_campaign`,
  getCodes: `${BASE}.get_influencer_codes`,
  createCode: `${BASE}.create_influencer_code`,
  validateCode: `${BASE}.validate_code`,
  getAnalytics: `${BASE}.get_marketing_analytics`,
};

const extract = (res) =>
  res.data?.message && typeof res.data.message === 'object'
    ? res.data.message
    : res.data?.message || res.data;

const extractErr = (err) => {
  const d = err.response?.data;
  if (d?._server_messages) {
    try {
      const msgs = JSON.parse(d._server_messages);
      const first = Array.isArray(msgs) ? msgs[0] : null;
      if (first) {
        const p = typeof first === 'string' ? JSON.parse(first) : first;
        if (p?.message) return String(p.message).replace(/<[^>]*>/g, '').trim();
      }
    } catch { /* fall through */ }
  }
  return d?.message || err.message || 'An error occurred';
};

export const getCampaigns = createAsyncThunk('marketing/campaigns', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getCampaigns, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const createCampaign = createAsyncThunk('marketing/createCampaign', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.createCampaign, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Campaign created', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const updateCampaign = createAsyncThunk('marketing/updateCampaign', async ({ campaignId, ...data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.updateCampaign, { campaign_id: campaignId, ...data });
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Campaign updated', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const getInfluencerCodes = createAsyncThunk('marketing/codes', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getCodes, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const createInfluencerCode = createAsyncThunk('marketing/createCode', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.createCode, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Code created', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const validateInfluencerCode = createAsyncThunk('marketing/validateCode', async (code, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.validateCode, { params: { code } });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const getMarketingAnalytics = createAsyncThunk('marketing/analytics', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getAnalytics, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

const marketingSlice = createSlice({
  name: 'marketing',
  initialState: {
    campaigns: [],
    campaignsTotal: 0,
    campaignOptions: [],   // active campaigns for dropdowns (fetched with page_size: 0)
    influencerCodes: [],
    codesTotal: 0,
    analytics: { influencer_stats: [], campaign_stats: [] },
    loading: false,
    error: null,
  },
  reducers: { clearError: (state) => { state.error = null; } },
  extraReducers: (builder) => {
    const loading = (state) => { state.loading = true; state.error = null; };
    const failed = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(getCampaigns.pending, loading)
      .addCase(getCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        const campaigns = action.payload?.campaigns || [];
        const total = action.payload?.total ?? campaigns.length;
        const pageSize = action.meta?.arg?.page_size;
        if (!pageSize || Number(pageSize) === 0) {
          // All-records fetch for dropdown options
          state.campaignOptions = campaigns;
        } else {
          // Paginated fetch for table
          state.campaigns = campaigns;
          state.campaignsTotal = total;
        }
      })
      .addCase(getCampaigns.rejected, failed)
      .addCase(createCampaign.fulfilled, (state, action) => {
        if (action.payload?.campaign) {
          state.campaigns.unshift(action.payload.campaign);
          state.campaignsTotal += 1;
          // Keep campaignOptions fresh if it was active
          if (action.payload.campaign.is_active) {
            state.campaignOptions.unshift(action.payload.campaign);
          }
        }
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        const c = action.payload?.campaign;
        if (c) {
          const idx = state.campaigns.findIndex(x => x.name === c.name);
          if (idx >= 0) state.campaigns[idx] = c;
          const optIdx = state.campaignOptions.findIndex(x => x.name === c.name);
          if (c.is_active && optIdx < 0) {
            state.campaignOptions.unshift(c);
          } else if (!c.is_active && optIdx >= 0) {
            state.campaignOptions.splice(optIdx, 1);
          } else if (optIdx >= 0) {
            state.campaignOptions[optIdx] = c;
          }
        }
      })
      .addCase(getInfluencerCodes.pending, loading)
      .addCase(getInfluencerCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.influencerCodes = action.payload?.codes || [];
        state.codesTotal = action.payload?.total ?? (action.payload?.codes || []).length;
      })
      .addCase(getInfluencerCodes.rejected, failed)
      .addCase(createInfluencerCode.fulfilled, (state, action) => {
        if (action.payload?.influencer_code) state.influencerCodes.unshift(action.payload.influencer_code);
      })
      .addCase(getMarketingAnalytics.pending, loading)
      .addCase(getMarketingAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = {
          influencer_stats: action.payload?.influencer_stats || [],
          campaign_stats: action.payload?.campaign_stats || [],
        };
      })
      .addCase(getMarketingAnalytics.rejected, failed);
  },
});

export const { clearError } = marketingSlice.actions;
export default marketingSlice.reducer;
